import { useCallback, useState } from 'react';
import useTableSort from '../../hooks/useTableSort';
import { translate } from '../../utils';
import { usePortfolioDashboard } from './PortfolioDashboardContext';
import { PortfolioHotelRankingRow } from './PortfolioHotelRankingRow';
import { getMetricPreviousComparisonTranslationKey, getMetricNextComparisonTranslationKey, INITIAL_VISIBLE_HOTEL_LIMIT } from './portfolioDashboardUtilities';

const SORT_FIELD_NAME_OCCUPANCY = 'occupancy';
const SORT_FIELD_NAME_REVENUE = 'revenue';
const SORT_FIELD_NAME_ADR = 'adr';
const SORT_FIELD_NAME_REVPAR = 'revPAR';
const SORT_FIELD_NAME_OCCUPIED_ROOM_COUNT = 'occupiedRoomCount';

/** @param {{ hotelRanking: import('./portfolioDashboard.types').PortfolioHotelRanking[], currency: string }} props */
export const PortfolioHotelRanking = function PortfolioHotelRanking({ hotelRanking, currency }) {
    const [showAllHotels, setShowAllHotels] = useState(false);
    const { sortedRows: rankedHotels, sortByField } = useTableSort(hotelRanking, SORT_FIELD_NAME_OCCUPANCY);
    const { period, isTodaySelected } = usePortfolioDashboard();
    const comparisonLegend = (
        <div className="font-size-11 text-muted normal no-br">
            {translate('frontend', getMetricPreviousComparisonTranslationKey(period, isTodaySelected))}
            <span className="mx-1">|</span>
            {translate('frontend', getMetricNextComparisonTranslationKey(period, isTodaySelected))}
        </div>
    );
    const visibleHotels = showAllHotels ? rankedHotels : rankedHotels.slice(0, INITIAL_VISIBLE_HOTEL_LIMIT);
    const hasMoreHotelsThanInitialVisibleHotelLimit = rankedHotels.length > INITIAL_VISIBLE_HOTEL_LIMIT;

    const handleToggleShowAllHotels = useCallback(() => {
        setShowAllHotels((previousShowAllHotels) => !previousShowAllHotels);
    }, []);

    return (
        <div className="box box-solid mb-0">
            <div className="box-header">
                <h3 className="box-title">
                    {translate('frontend', 'PORTFOLIO_DASHBOARD_HOTEL_RANKING')}
                </h3>
            </div>

            <div className="box-body">
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="portfolio-dashboard__rank-cell"></th>
                                <th>{translate('frontend', 'PORTFOLIO_DASHBOARD_HOTEL')}</th>
                                <th className="text-right">
                                    <a
                                        className="cursor-pointer"
                                        onClick={() => sortByField(SORT_FIELD_NAME_OCCUPANCY)}
                                    >
                                        {translate('frontend', 'PORTFOLIO_DASHBOARD_OCCUPANCY')}
                                    </a>

                                    {comparisonLegend}
                                </th>
                                <th className="text-right">
                                    <a
                                        className="cursor-pointer"
                                        onClick={() => sortByField(SORT_FIELD_NAME_REVENUE)}
                                    >
                                        {translate('frontend', 'PORTFOLIO_DASHBOARD_REVENUE')}
                                    </a>

                                    {comparisonLegend}
                                </th>
                                <th className="text-right">
                                    <a
                                        className="cursor-pointer"
                                        onClick={() => sortByField(SORT_FIELD_NAME_ADR)}
                                    >
                                        {translate('frontend', 'PORTFOLIO_DASHBOARD_ADR')}
                                    </a>

                                    {comparisonLegend}
                                </th>
                                <th className="text-right">
                                    <a
                                        className="cursor-pointer"
                                        onClick={() => sortByField(SORT_FIELD_NAME_REVPAR)}
                                    >
                                        {translate('frontend', 'PORTFOLIO_DASHBOARD_REVPAR')}
                                    </a>

                                    {comparisonLegend}
                                </th>
                                <th className="text-right">
                                    <a
                                        className="cursor-pointer"
                                        onClick={() => sortByField(SORT_FIELD_NAME_OCCUPIED_ROOM_COUNT)}
                                    >
                                        {translate('frontend', 'PORTFOLIO_DASHBOARD_OCCUPIED_ROOMS')}
                                    </a>

                                    {comparisonLegend}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleHotels.map((hotel, index) => (
                                <PortfolioHotelRankingRow
                                    key={hotel.hotelKey}
                                    rank={index + 1}
                                    hotel={hotel}
                                    currency={currency}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                {hasMoreHotelsThanInitialVisibleHotelLimit ? (
                    <button type="button" className="btn btn-default btn-sm w-full mt-2" onClick={handleToggleShowAllHotels}>
                        {translate('frontend', showAllHotels ? 'PORTFOLIO_DASHBOARD_SHOW_LESS' : 'PORTFOLIO_DASHBOARD_SHOW_MORE')}
                    </button>
                ) : null}
            </div>
        </div>
    );
};
